import React, { useEffect, useState } from "react";
import api, { apiError } from "@/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { FileText, Image as ImageIcon, Eye, Trash2, UploadCloud, CalendarDays, Inbox, Send, Search } from "lucide-react";

const fmtDate = (d) => {
  if (!d) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
  if (m) {
    const y = +m[1];
    if (y >= 1350 && y <= 1550) return `${m[3]} / ${m[2]} / ${m[1]} هـ`;
    try { return new Date(d).toLocaleDateString("ar-SA-u-ca-gregory", { year: "numeric", month: "long", day: "numeric" }); }
    catch { return d; }
  }
  return d;
};
const fmtSize = (n) => (!n ? "" : n > 1048576 ? `${(n / 1048576).toFixed(1)} م.ب` : `${Math.round(n / 1024)} ك.ب`);
const isPdf = (c) => (c.content_type || "").includes("pdf");

export default function Circulars() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [search, setSearch] = useState("");
  const [monthFilter, setMonthFilter] = useState("all");

  const load = async () => {
    try {
      const { data } = await api.get("/circulars");
      setItems(data);
      if (!isAdmin && data.length) {
        const latest = data.reduce((mx, c) => ((c.created_at || "") > mx ? c.created_at : mx), "");
        localStorage.setItem("circulars_seen_at", latest);
        window.dispatchEvent(new Event("circulars-seen"));
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !issueDate || !file) { toast.error("أدخل العنوان وتاريخ الإصدار واختر الملف"); return; }
    const fd = new FormData();
    fd.append("title", title.trim());
    fd.append("issue_date", issueDate);
    fd.append("file", file);
    setUploading(true);
    try {
      await api.post("/circulars", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("تمت إضافة التعميم بنجاح");
      setTitle(""); setIssueDate(""); setFile(null);
      const el = document.getElementById("circular-file-input"); if (el) el.value = "";
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploading(false); }
  };

  const viewFile = async (c) => {
    setBusyId(c.id);
    try {
      const res = await api.get(`/circulars/${c.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusyId(null); }
  };

  const remove = async (c) => {
    if (!window.confirm(`هل تريد حذف التعميم «${c.title}»؟`)) return;
    try { await api.delete(`/circulars/${c.id}`); toast.success("تم حذف التعميم"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const months = [...new Set(items.map((c) => (c.issue_date || "").slice(0, 7)).filter(Boolean))].sort().reverse();
  const monthLabel = (ym) => { const [y, m] = ym.split("-"); return +y >= 1350 && +y <= 1550 ? `${m} / ${y} هـ` : ym; };
  const filtered = items.filter((c) =>
    (!search.trim() || (c.title || "").includes(search.trim())) &&
    (monthFilter === "all" || (c.issue_date || "").startsWith(monthFilter))
  );

  return (
    <div className="space-y-6" data-testid="circulars-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-b from-sky-500 to-sky-600 flex items-center justify-center text-white shadow-lg">
          {isAdmin ? <Send size={22} /> : <Inbox size={22} />}
        </div>
        <div>
          <h1 className="text-2xl font-extrabold text-stone-800">{isAdmin ? "التعاميم الصادرة" : "التعاميم الواردة"}</h1>
          <p className="text-sm text-stone-500">{isAdmin ? "أضف التعاميم الصادرة من الإدارة لتصل جميع المنسقين تلقائياً" : "التعاميم الصادرة من الإدارة العامة، مرتّبة حسب تاريخ الإصدار"}</p>
        </div>
      </div>

      {isAdmin && (
        <form onSubmit={submit} data-testid="circular-form" className="bg-white/70 backdrop-blur rounded-2xl border border-stone-200/70 p-5 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-stone-700 mb-1 block">عنوان التعميم</label>
              <input data-testid="circular-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="مثال: تعميم بشأن أسبوع التطوع" className="w-full rounded-xl border border-stone-300 px-3 py-2 focus:border-sky-400 outline-none" />
            </div>
            <div>
              <label className="text-sm font-semibold text-stone-700 mb-1 block">تاريخ الإصدار (هجري)</label>
              <input data-testid="circular-date-input" type="text" inputMode="numeric" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} placeholder="مثال: 24/03/1448" className="w-full rounded-xl border border-stone-300 px-3 py-2 focus:border-sky-400 outline-none" />
            </div>
          </div>
          <div>
            <label className="text-sm font-semibold text-stone-700 mb-1 block">ملف التعميم (PDF أو صورة JPG/PNG)</label>
            <input id="circular-file-input" data-testid="circular-file-input" type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="w-full rounded-xl border border-stone-300 px-3 py-2 text-sm" />
            <p className="text-xs text-stone-400 mt-1">يتم ضغط الصور تلقائياً مع الحفاظ على الجودة لتوفير مساحة التخزين.</p>
          </div>
          <button data-testid="circular-upload-btn" type="submit" disabled={uploading} className="btn-gold px-6 py-2.5 rounded-xl inline-flex items-center gap-2 disabled:opacity-50">
            <UploadCloud size={18} /> {uploading ? "جارٍ الرفع..." : "إضافة التعميم"}
          </button>
        </form>
      )}

      {!loading && items.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-3" data-testid="circulars-filters">
          <div className="relative flex-1">
            <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input data-testid="circular-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="ابحث في عناوين التعاميم..." className="w-full rounded-xl border border-stone-300 pr-9 pl-3 py-2 focus:border-sky-400 outline-none" />
          </div>
          <select data-testid="circular-month-filter" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} className="rounded-xl border border-stone-300 px-3 py-2 focus:border-sky-400 outline-none bg-white">
            <option value="all">كل الأشهر</option>
            {months.map((ym) => <option key={ym} value={ym}>{monthLabel(ym)}</option>)}
          </select>
        </div>
      )}

      {loading ? (
        <p className="text-stone-500 text-center py-10">جارٍ التحميل...</p>
      ) : items.length === 0 ? (
        <div data-testid="circulars-empty" className="text-center py-14 text-stone-400">
          <Inbox size={40} className="mx-auto mb-3 opacity-50" />
          لا توجد تعاميم حتى الآن
        </div>
      ) : filtered.length === 0 ? (
        <div data-testid="circulars-no-results" className="text-center py-14 text-stone-400">لا توجد تعاميم مطابقة للبحث أو الشهر المحدد</div>
      ) : (
        <div className="bg-white/70 backdrop-blur rounded-2xl border border-stone-200/70 overflow-hidden" data-testid="circulars-table">
          <div className="overflow-x-auto">
            <table className="w-full text-right border-collapse min-w-[640px]">
              <thead>
                <tr className="bg-sky-50/80 text-sky-900 text-sm">
                  <th className="py-3 px-3 font-bold w-12">م</th>
                  <th className="py-3 px-3 font-bold">عنوان التعميم</th>
                  <th className="py-3 px-3 font-bold w-40">تاريخ الإصدار</th>
                  <th className="py-3 px-3 font-bold w-24">النوع</th>
                  <th className="py-3 px-3 font-bold w-24">الحجم</th>
                  <th className="py-3 px-3 font-bold w-40">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c, i) => (
                  <tr key={c.id} data-testid={`circular-item-${c.id}`} className="border-t border-stone-100 hover:bg-sky-50/40 transition-colors">
                    <td className="py-3 px-3 text-stone-400 text-sm">{i + 1}</td>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2 font-semibold text-stone-800">
                        <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isPdf(c) ? "bg-red-50 text-red-500" : "bg-emerald-50 text-emerald-500"}`}>
                          {isPdf(c) ? <FileText size={16} /> : <ImageIcon size={16} />}
                        </span>
                        <span className="min-w-0">{c.title}</span>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-stone-700 text-sm whitespace-nowrap">
                      <span className="inline-flex items-center gap-1"><CalendarDays size={14} className="text-sky-500" /> {fmtDate(c.issue_date)}</span>
                    </td>
                    <td className="py-3 px-3 text-stone-500 text-sm">{isPdf(c) ? "PDF" : "صورة"}</td>
                    <td className="py-3 px-3 text-stone-500 text-sm whitespace-nowrap">{fmtSize(c.size)}</td>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2">
                        <button data-testid={`circular-view-${c.id}`} onClick={() => viewFile(c)} disabled={busyId === c.id} className="px-3 py-1.5 rounded-lg bg-sky-50 text-sky-700 hover:bg-sky-100 inline-flex items-center gap-1.5 text-sm font-semibold disabled:opacity-50">
                          <Eye size={15} /> {busyId === c.id ? "..." : "عرض"}
                        </button>
                        {isAdmin && (
                          <button data-testid={`circular-delete-${c.id}`} onClick={() => remove(c)} className="px-3 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 inline-flex items-center gap-1.5 text-sm font-semibold">
                            <Trash2 size={15} /> حذف
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
